import React from 'react';
import ReactDOM from 'react-dom';
import {
    constructRoutes,
    constructApplications,
    constructLayoutEngine
} from 'single-spa-layout';
import {
    registerApplication,
    start,
    getAppStatus,
    getMountedApps,
    getAppNames,
    pathToActiveWhen
} from 'single-spa';
import { pulseliveSSO, READY_EVENT } from '@pulselive/keycloak-sdk-wrapper';
import { ReactComponent as applicationLogo } from './assets/exp-logo.svg';
import { checkPermission } from './helper/helper';
import { CookieBanner } from '@pulselive/core-product-components';
import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import { CaptureConsole as CaptureConsoleIntegration } from '@sentry/integrations';
import { checkFeatureFlag } from './helper/feature-flag';
import './styles.scss';

const APP_ENV = getEnv();

const initSentry = function initSentry() {

    // to save on error quota, for now don't track errors on dev
    if (APP_ENV === 'dev') {
        return;
    }

    Sentry.init({
        dsn: 'https://3c9d69bec61546868108dccc127baaf0@o4504101252104192.ingest.sentry.io/4504101938135040',
        integrations: [
            new BrowserTracing(),
            new CaptureConsoleIntegration({
                levels: ['warn', 'error', 'debug', 'assert']
            })
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        // eslint-disable-next-line no-magic-numbers
        tracesSampleRate: 0.25,
        environment: APP_ENV || 'dev'
    });
};

ReactDOM.render(
    <CookieBanner onAgreeCallback={cookieAccept} />,
    document.getElementById('cookieNotice')
);

/**
 * @returns {string} env name
 */
function getEnv() {
    let env = '';

    if (process?.env?.dev) {
        env = 'dev';
    }
    if (process?.env?.test) {
        env = 'test';
    }
    if (process?.env?.uat) {
        env = 'uat';
    }
    if (process?.env?.production) {
        env = 'production';
    }

    return env;
}

/**
 *
 */
function cookieAccept() {
    initSentry();
    runHotjar();
}

if (CookieBanner.getCookieStatus()) {
    initSentry();
}

// client logo
const clientLogo = './resources/logo-icon.svg';

import applicationRoutes, { navItemsData } from './routes';
import { getSiteAccounts } from './helper/accounts';

const ERROR_ID = 'error';
const LOADER_ID = 'loader';
const ERROR_401 = 401;

const loader = document.getElementById(LOADER_ID);

const newExpAuthErrorTemplate = `<div class="auth-error">
        <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.47012 18.0002H17.5301C19.0701 18.0002 20.0301 16.3302 19.2601 15.0002L11.7301 1.99018C10.9601 0.660176 9.04012 0.660176 8.27012 1.99018L0.740121 15.0002C-0.0298788 16.3302 0.930121 18.0002 2.47012 18.0002V18.0002ZM10.0001 11.0002C9.45012 11.0002 9.00012 10.5502 9.00012 10.0002V8.00018C9.00012 7.45018 9.45012 7.00018 10.0001 7.00018C10.5501 7.00018 11.0001 7.45018 11.0001 8.00018V10.0002C11.0001 10.5502 10.5501 11.0002 10.0001 11.0002ZM11.0001 15.0002H9.00012V13.0002H11.0001V15.0002Z" fill="#DE350D"/>
        </svg>
        <h1 class="error">Service temporarily unavailable</h1>
        <p>Sorry, we can't verify your access to the Experience Platform.<br>Possible causes: recent permission changes or incorrect permissions.<br>If it continues, please inform your client lead.<br>Thank you for your patience.</p>
        </div>
    `;

const permissionErrorTemplate = `<div class="auth-error">
    <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.47012 18.0002H17.5301C19.0701 18.0002 20.0301 16.3302 19.2601 15.0002L11.7301 1.99018C10.9601 0.660176 9.04012 0.660176 8.27012 1.99018L0.740121 15.0002C-0.0298788 16.3302 0.930121 18.0002 2.47012 18.0002V18.0002ZM10.0001 11.0002C9.45012 11.0002 9.00012 10.5502 9.00012 10.0002V8.00018C9.00012 7.45018 9.45012 7.00018 10.0001 7.00018C10.5501 7.00018 11.0001 7.45018 11.0001 8.00018V10.0002C11.0001 10.5502 10.5501 11.0002 10.0001 11.0002ZM11.0001 15.0002H9.00012V13.0002H11.0001V15.0002Z" fill="#4D2BCC"/>
    </svg>
    <h1 class="info">No access</h1>
    <p>You do not have the correct permissions to access this.<br>Please contact your client lead to resolve.</p>
    </div>
`;

const permissionErrorInternal = `<div class="auth-error">
    <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.47012 18.0002H17.5301C19.0701 18.0002 20.0301 16.3302 19.2601 15.0002L11.7301 1.99018C10.9601 0.660176 9.04012 0.660176 8.27012 1.99018L0.740121 15.0002C-0.0298788 16.3302 0.930121 18.0002 2.47012 18.0002V18.0002ZM10.0001 11.0002C9.45012 11.0002 9.00012 10.5502 9.00012 10.0002V8.00018C9.00012 7.45018 9.45012 7.00018 10.0001 7.00018C10.5501 7.00018 11.0001 7.45018 11.0001 8.00018V10.0002C11.0001 10.5502 10.5501 11.0002 10.0001 11.0002ZM11.0001 15.0002H9.00012V13.0002H11.0001V15.0002Z" fill="#4D2BCC"/>
    </svg>
    <h1 class="info">No access</h1>
    <p>You do not have the correct permissions to access this.<br>Please get in touch with your client lead to resolve.</p>
    </div>
`;


const showErrorHelper = (template) => {
    const errorAlert = document.getElementById(ERROR_ID);
    errorAlert.style.display = 'block';
    errorAlert.insertAdjacentHTML( 'beforeend', template );
    loader.style.display = 'none';
}

pulseliveSSO.initiated
    ? startWhenAuthed()
    : document.addEventListener(READY_EVENT, startWhenAuthed);

/**
 * Start When Authenticated
 *
 * When the user is authenticated via the keycloak SDK, start the app.
 */
function startWhenAuthed() {
    if (pulseliveSSO.keycloak.authenticated) {
        const headers = {
            ...pulseliveSSO.getAuthHeader()
        };

        const apiUrl = pulseliveSSO.config['api-url'];

        fetch(`${apiUrl}/auth/user`, { headers })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === ERROR_401) {
                        pulseliveSSO.getUserProfile().then( user => {
                            if (user.email.includes('@pulselive.com')) {
                                showErrorHelper(permissionErrorInternal);
                            } else {
                                showErrorHelper(permissionErrorTemplate);
                            }
                        });
                        return null;
                    }
                }
                return response.json();
            })
            .then((userResponse) => {
                if (
                    userResponse?.permissions &&
                    Object.keys(userResponse.permissions)
                ) {
                    const permissions =
                        userResponse.permissions[
                            Object.keys(userResponse.permissions)[0]
                        ];

                    const navItemsWithPermissions = navItemsData
                        .map((item) => checkPermission(item, permissions))
                        .filter((item) => !!item);

                    startApp(navItemsWithPermissions);
                }
            })
            .catch((error) => {
                showErrorHelper(newExpAuthErrorTemplate);
                console.error(error);
            });

        // init Pendo
        const cookieBannerStatus = CookieBanner.getCookieStatus();
        if (cookieBannerStatus) {
            runPendo();
            runHotjar();
        }

        return;
    }

    // Redirects user to login SSO page
    pulseliveSSO.login();
}

/*
 * Set env for keycloak sdk/adapter on localhost as default behaviour is linked to FDP.
 * On deployed code this will be based off the domain.
 */
if (
    !location.host ||
    location.host.match(/localhost/g) ||
    location.host.match(/local./g)
) {
    window.localStorage.setItem(
        'KEYCLOAK_ADAPTER_ENV',
        'exp.premierleague.dev.pulselive.com'
    );
}

/**
 * Init Pendo
 */
function runPendo() {
    const { email, name } = pulseliveSSO.getUserInfo();
    window.pendo.initialize({
        visitor: {
            id: pulseliveSSO.keycloak.subject,
            full_name: name, // eslint-disable-line camelcase
            email
        },
        account: {
            id: pulseliveSSO.realm
        }
    });
}

/**
 * Init Hotjar
 */
function runHotjar() {
    if (APP_ENV === 'dev') {
        // no hotjar tracking on dev
        return;
    }
    /* eslint-disable */
    // heat up them jars
    (function (h, o, t, j, a, r) {
        h.hj =
            h.hj ||
            function () {
                (h.hj.q = h.hj.q || []).push(arguments);
            };
        h._hjSettings = { hjid: 844119, hjsv: 6 };
        a = o.getElementsByTagName('head')[0];
        r = o.createElement('script');
        r.async = 1;
        // append style fix to stop EXP inline styles breaking the embed
        const style = o.createElement('style');
        style.textContent = ` ._hj_feedback_container fieldset { display: block !important; } `;
        // -------------------------------------------------------------------------------------------------- //
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
        a.appendChild(style);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
    /* eslint-enable */
}

/**
 * Start App
 *
 * SingleSpa bootstrapping code. Initialises browser routing and appropriate
 * apps.
 *
 * @param {object} navItemsWithPermissions - filtered nav items based on permission
 */
async function startApp(navItemsWithPermissions) {
    // hide basic loader when app starts
    loader.style.display = 'none';

    // set this value so angular app knows to render its new components e.g. sidebar
    localStorage.setItem('experience-platform-layout', true);

    const blacklistCMSUI = [
        pathToActiveWhen('/visual-editor'),
        pathToActiveWhen('/page-builder'),
        pathToActiveWhen('/content-2'),
    
    ];
    const blacklistAuthUI = [
        pathToActiveWhen('/visual-editor'),
        pathToActiveWhen('/page-builder')
    ];

    // if route has a featureFlag prop run request to check flag status
    const checkRoutes = await Promise.all(applicationRoutes.map(async (route) => {
        if (!route.featureFlag) {
            // no featureFlag prop so just return route as is
            return route;
        }
        
        // run the request
        const enabled = await checkFeatureFlag(route.featureFlag);
        if (enabled) {
            // remove feature flag prop as its not valid config
            delete route.featureFlag;
            // add route to blacklist so route will load with microfrontend module
            blacklistCMSUI.push( pathToActiveWhen( route.path ) );
            return route;
        }
        
        return null; 
    }));
    // remove any items that have been returned null because flag was disabled
    const availableRoutes = checkRoutes.filter( route => route !== null);

    const accountSwitcherAccounts = getSiteAccounts( pulseliveSSO.realm, APP_ENV );
    const accountSwitcherConfig = accountSwitcherAccounts ? {
        accounts: accountSwitcherAccounts,
        flag: await checkFeatureFlag( 'auth-ui.account.switcher' )
    } : null;

    const routes = constructRoutes({
        routes: [
            {
                type: 'application',
                name: '@pulselive/auth-ui',
                props: {
                    applicationRoutes: navItemsWithPermissions,
                    applicationLogo,
                    clientLogo,
                    showProductSwitcher: true,
                    accountSwitcher: accountSwitcherConfig
                }
            },
            ...availableRoutes
        ]
    });

    const applications = constructApplications({
        routes,
        loadApp: ({ name }) => System.import(name)
    });


    /**
     * By default we load the cms-ui component on the home route as most of the application
     * currently is routed via the cms-ui. However on certain new routes (see blacklist)
     * we don't want the legacy cms-ui to be loaded. For the time being the following function
     * is used for this. Down the line as we replace more of the cms-ui with new components
     * we would simply only load the cms-ui on sub routes rather than on the default `/` route.
     *
     * This activeWhen function is passed a location when the URL changes and
     * will test if the location fits any of the blacklist criteria. If any of
     * the blacklist items are true then activeWhen should return false.
     */

    applications.forEach((appItem) => {
        if (appItem.name === '@pulselive/cms-ui') {
            appItem.activeWhen = (location) => {
                const list = blacklistCMSUI.map((isBlacklisted) =>
                    isBlacklisted(new URL(location.href))
                );
                return list.some((item) => item === true) ? false : true;
            };
        }
        if (appItem.name === '@pulselive/auth-ui') {
            appItem.activeWhen = (location) => {
                const list = blacklistAuthUI.map((isBlacklisted) =>
                    isBlacklisted(new URL(location.href))
                );
                return list.some((item) => item === true) ? false : true;
            };
        }
    });

    const layoutEngine = constructLayoutEngine({
        routes,
        applications,
        active: false
    });

    layoutEngine.activate();

    applications.forEach(registerApplication);

    start({
        urlRerouteOnly: true
    });

    if ('serviceWorker' in navigator && !process.env.dev) {
        navigator.serviceWorker.register('/service-worker.js');
    }

    /**
     * Export some singleSpa utility functions to the window.
     * This facilitates E2E unit tests for the app.
     */
    window.getAppStatus = getAppStatus;
    window.getAppNames = getAppNames;
    window.getMountedApps = getMountedApps;
}
